/** Main App component with routing and authentication. */
import React, { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Login from "./pages/Login";
import ChangePassword from "./pages/ChangePassword";
import Dashboard from "./pages/Dashboard";
import Suppliers from "./pages/Suppliers";
import Billing from "./pages/Billing";
import Invoices from "./pages/Invoices";
import Payments from "./pages/Payments";
import TaxReports from "./pages/TaxReports";
import BusinessKPIs from "./pages/BusinessKPIs";
import Gateways from "./pages/Gateways";
import TrafficAnalysis from "./pages/TrafficAnalysis";
import FinancialReports from "./pages/FinancialReports";
import ApiKeys from "./pages/ApiKeys";
import AccountsPage from "./pages/AccountsPage";
import ResellerList from "./pages/resellers/ResellerList";
import WholesaleClients from "./pages/resellers/WholesaleClients";
import PINCustomers from "./pages/resellers/PINCustomers";
import RouteWorkspace from "./pages/RouteWorkspace";
import CGRatesStatusPage from "./pages/CGRatesStatusPage";
import RatePlanList from "./pages/rate-plans/RatePlanList";
import RatePlanDetail from "./pages/rate-plans/RatePlanDetail";
import RateComparison from "./pages/rate-plans/RateComparison";
import DashboardLayout from "./components/layout/DashboardLayout";
import { NotificationProvider } from "./components/common/NotificationProvider";
import ResellerLogin from "./components/resellers/ResellerLogin";
import AcceptInvite from "./pages/resellers/AcceptInvite";
import ResellerDashboardLayout from "./components/resellers/ResellerDashboardLayout";
import ResellerAccountsPage from "./pages/resellers/Accounts";
import ResellerCDRsPage from "./pages/resellers/CDRs";
import ResellerDashboardPage from "./pages/resellers/Dashboard";
import ResellerTransferPage from "./pages/resellers/Transfer";
import ResellerProfilePage from "./pages/resellers/Profile";
import CustomerPortal from "./pages/customer/CustomerPortal";
import TestCall from "./pages/TestCall";
import ResellerRatesPage from "./pages/resellers/Rates";
import ResellerReportsPage from "./pages/resellers/Reports";
import CDRs from "./pages/CDRs";
import ActiveCalls from "./pages/ActiveCalls";
import Alerts from "./pages/Alerts";
import AuditLog from "./pages/AuditLog";
import Team from "./pages/admin/Team";
import ServerDetail from "./pages/ServerDetail";
import ErrorBoundary from "./components/common/ErrorBoundary";

/** Toggle light/dark; persists + notifies the App. */
export function toggleThemeMode() {
  const next = (localStorage.getItem("theme_mode") || "light") === "dark" ? "light" : "dark";
  localStorage.setItem("theme_mode", next);
  window.dispatchEvent(new Event("theme-mode-changed"));
}

/** Get reseller token from localStorage. */
const getResellerToken = (): string | null => {
  return localStorage.getItem("reseller_token");
};

/** Wave 6: dark mode — persisted in localStorage (theme_mode). */
function buildTheme(mode: "light" | "dark") {
  return createTheme({
    palette: {
      mode,
      ...(mode === "dark"
        ? { background: { default: "#121826", paper: "#1a2232" } }
        : {}),
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#dc004e",
    },
    },
  });
}

/** Protected route component that checks for JWT token. */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  // Optimistic sync check; the layout validates the session via /auth/me
  // on mount and bounces to login with ?expired=1 when the cookie is dead.
  const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/** Protected reseller route component that checks for reseller JWT token. */
function ProtectedResellerRoute({ children }: { children: React.ReactNode }) {
  const resellerToken = getResellerToken();

  if (!resellerToken) {
    return <Navigate to="/reseller/login" replace />;
  }

  return <>{children}</>;
}

/** Main App component with routes. */
function App() {
  const [mode, setMode] = useState<"light" | "dark">(
    () => (localStorage.getItem("theme_mode") as "light" | "dark") || "light"
  );
  useEffect(() => {
    const handler = () => setMode((localStorage.getItem("theme_mode") as "light" | "dark") || "light");
    window.addEventListener("theme-mode-changed", handler);
    return () => window.removeEventListener("theme-mode-changed", handler);
  }, []);
  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
      <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* Admin routes */}
          <Route path="/login" element={<Login />} />
          {/* A1 (GAP V2): forced credential rotation — shared by admin + reseller sessions */}
          <Route path="/change-password" element={<ChangePassword />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Dashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Dashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/servers/:id"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ServerDetail />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/carriers"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Navigate to="/suppliers" replace />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/suppliers"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Suppliers />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AccountsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/resellers"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ResellerList />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/wholesale"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <WholesaleClients />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pin-customers"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <PINCustomers />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cdrs"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CDRs />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/active-calls"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ActiveCalls />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Billing />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Invoices />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Payments />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tax-reports"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <TaxReports />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/routing"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <RouteWorkspace />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/lcr"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Navigate to="/routing" replace />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/test-call"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <TestCall />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/alerts"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Alerts />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AuditLog />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/team"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Team />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cgrates/status"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CGRatesStatusPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/rate-plans"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <RatePlanList />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/rate-plans/:id"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <RatePlanDetail />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/rate-comparison"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <RateComparison />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          {/* Settings/Users hidden (Wave 1.7); API keys now real (Wave 5.7) */}
          <Route
            path="/api-keys"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ApiKeys />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/business-kpis"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <BusinessKPIs />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/gateways"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Gateways />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/traffic"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <TrafficAnalysis />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/financial-reports"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <FinancialReports />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          {/* Reseller routes */}
          <Route path="/reseller/login" element={<ResellerLogin />} />
          <Route path="/reseller/accept" element={<AcceptInvite />} />
          <Route path="/customer/login" element={<ResellerLogin />} />
          <Route
            path="/customer/portal"
            element={
              <ProtectedResellerRoute>
                <ResellerDashboardLayout>
                  <CustomerPortal />
                </ResellerDashboardLayout>
              </ProtectedResellerRoute>
            }
          />
          <Route
            path="/reseller/dashboard"
            element={
              <ProtectedResellerRoute>
                <ResellerDashboardLayout>
                  <ResellerDashboardPage />
                </ResellerDashboardLayout>
              </ProtectedResellerRoute>
            }
          />
          <Route
            path="/reseller/accounts"
            element={
              <ProtectedResellerRoute>
                <ResellerDashboardLayout>
                  <ResellerAccountsPage />
                </ResellerDashboardLayout>
              </ProtectedResellerRoute>
            }
          />
          <Route
            path="/reseller/cdrs"
            element={
              <ProtectedResellerRoute>
                <ResellerDashboardLayout>
                  <ResellerCDRsPage />
                </ResellerDashboardLayout>
              </ProtectedResellerRoute>
            }
          />
          <Route
            path="/reseller/transfer"
            element={
              <ProtectedResellerRoute>
                <ResellerDashboardLayout>
                  <ResellerTransferPage />
                </ResellerDashboardLayout>
              </ProtectedResellerRoute>
            }
          />
          <Route
            path="/reseller/reports"
            element={
              <ProtectedResellerRoute>
                <ResellerDashboardLayout>
                  <ResellerReportsPage />
                </ResellerDashboardLayout>
              </ProtectedResellerRoute>
            }
          />
          <Route
            path="/reseller/rates"
            element={
              <ProtectedResellerRoute>
                <ResellerDashboardLayout>
                  <ResellerRatesPage />
                </ResellerDashboardLayout>
              </ProtectedResellerRoute>
            }
          />
          <Route
            path="/reseller/profile"
            element={
              <ProtectedResellerRoute>
                <ResellerDashboardLayout>
                  <ResellerProfilePage />
                </ResellerDashboardLayout>
              </ProtectedResellerRoute>
            }
          />

          {/* Default redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </ErrorBoundary>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
